import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { ArrowLeft, Eye, EyeOff, Fingerprint, LockKeyhole, Mail } from "lucide-react";
import { api, ApiError } from "./api";
import { formatEmailInput } from "./input-masks";
import logoUrl from "../../../public/logo-ja.webp";
import { useSearchParams } from "react-router-dom";
import { authenticateWithPasskey, passkeyErrorMessage, passkeysAvailable } from "./passkeys";
import { clearAppLockRecord } from "./app-lock-state";

export type CurrentUser = {
  userId: string;
  organizationId: string;
  mfaVerified: boolean;
  email: string;
  employeeId: string | null;
  branchId: string | null;
  permissions: string[];
};

type AuthValue = {
  user: CurrentUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: (options?: { preserveAppLock?: boolean; allSessions?: boolean }) => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      setUser(await api<CurrentUser>("/auth/sessions/current"));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setUser(null);
      else throw error;
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    const frame = requestAnimationFrame(() => void refresh());
    return () => cancelAnimationFrame(frame);
  }, [refresh]);
  const value = useMemo<AuthValue>(() => ({
    user, loading, refresh,
    logout: async (options) => {
      try {
        await api(`/auth/sessions${options?.allSessions ? "?scope=all" : ""}`, { method: "DELETE" });
      } finally {
        if (!options?.preserveAppLock) clearAppLockRecord();
        setUser(null);
      }
    },
  }), [user, loading, refresh]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider ausente");
  return value;
};

const MFA_LENGTH = 6;

function MfaCodeInput({ digits, onChange, disabled }: {
  digits: string[];
  onChange: (digits: string[]) => void;
  disabled: boolean;
}) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const distribute = (start: number, rawValue: string) => {
    const incoming = rawValue.replace(/\D/g, "").slice(0, MFA_LENGTH - start);
    if (!incoming) return;
    const next = [...digits];
    for (const [offset, digit] of [...incoming].entries()) next[start + offset] = digit;
    onChange(next);
    const target = Math.min(start + incoming.length, MFA_LENGTH - 1);
    requestAnimationFrame(() => inputs.current[target]?.focus());
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      const next = [...digits];
      next[index - 1] = "";
      onChange(next);
      inputs.current[index - 1]?.focus();
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < MFA_LENGTH - 1) {
      event.preventDefault();
      inputs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>, index: number) => {
    event.preventDefault();
    distribute(index, event.clipboardData.getData("text"));
  };

  return <fieldset className="mfa-code-fieldset">
    <legend>Código de verificação</legend>
    <div className="mfa-code-input" role="group" aria-label="Código de seis dígitos">
      {digits.map((digit, index) => <input
        key={index}
        ref={(element) => { inputs.current[index] = element; }}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete={index === 0 ? "one-time-code" : "off"}
        enterKeyHint={index === MFA_LENGTH - 1 ? "done" : "next"}
        value={digit}
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => {
          const numeric = event.target.value.replace(/\D/g, "");
          if (numeric.length > 1) return distribute(index, numeric);
          const next = [...digits];
          next[index] = numeric;
          onChange(next);
          if (numeric && index < MFA_LENGTH - 1) inputs.current[index + 1]?.focus();
        }}
        onKeyDown={(event) => handleKeyDown(event, index)}
        onPaste={(event) => handlePaste(event, index)}
        aria-label={`Dígito ${index + 1} de ${MFA_LENGTH}`}
        maxLength={MFA_LENGTH}
        required
        disabled={disabled}
        autoFocus={index === 0}
      />)}
    </div>
  </fieldset>;
}

export function AuthScreenLayout({ children }: { children: ReactNode }) {
  return <main className="login-page">
    <section className="login-brand-panel" aria-label="JA Soluções Prediais">
      <div className="login-brand-inner">
        <span className="login-logo">
          <img className="login-logo-full" src={logoUrl} alt="JA Soluções Prediais" />
          <img className="login-logo-symbol" src="/favicon-ja.png" alt="JA" />
        </span>
        <div className="login-brand-copy">
          <span>Plataforma de gestão</span>
          <strong>Comercial · Operação · Financeiro</strong>
        </div>
      </div>
    </section>
    <section className="login-access-panel">
      {children}
      <footer className="login-access-footer">Acesso protegido · JA Soluções Prediais</footer>
    </section>
  </main>;
}

export function LoginPage() {
  const { refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const resetToken = searchParams.get("reset");
  const [mode, setMode] = useState<"login" | "request" | "reset">(resetToken ? "reset" : "login");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [codeDigits, setCodeDigits] = useState<string[]>(() => Array.from({ length: MFA_LENGTH }, () => ""));
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [biometricsAvailable, setBiometricsAvailable] = useState(false);
  const emailId = useId();
  const passwordId = useId();
  const newPasswordId = useId();
  const mfaComplete = codeDigits.every(Boolean);

  useEffect(() => { void passkeysAvailable().then(setBiometricsAvailable); }, []);

  const returnToLogin = () => {
    setMode("login");
    setChallengeId(null);
    setCodeDigits(Array.from({ length: MFA_LENGTH }, () => ""));
    setPassword("");
    setError("");
    setNotice("");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (mode === "request") {
        await api("/auth/password-reset-requests", { method: "POST", body: JSON.stringify({ email }) });
        setMode("login");
        setNotice("Se o e-mail estiver cadastrado, as instruções serão enviadas.");
      } else if (mode === "reset") {
        if (!resetToken) throw new Error("Link inválido");
        await api("/auth/password-resets", { method: "POST", body: JSON.stringify({ token: resetToken, newPassword }) });
        setMode("login");
        setNotice("Senha alterada. Entre novamente.");
      } else if (challengeId) {
        await api(`/auth/mfa/challenges/${challengeId}/verify`, { method: "POST", body: JSON.stringify({ code: codeDigits.join("") }) });
      } else {
        const result = await api<{ kind?: string; challengeId?: string }>("/auth/sessions", {
          method: "POST", body: JSON.stringify({ email, password }),
        });
        if (result.kind === "mfa_challenge" && result.challengeId) {
          setChallengeId(result.challengeId);
          setCodeDigits(Array.from({ length: MFA_LENGTH }, () => ""));
          return;
        }
      }
      if (mode === "login") {
        clearAppLockRecord();
        await refresh();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível entrar");
    } finally {
      setBusy(false);
    }
  };

  const biometricLogin = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      await authenticateWithPasskey();
      clearAppLockRecord();
      await refresh();
    }
    catch (caught) { setError(passkeyErrorMessage(caught, "Não foi possível entrar com biometria.")); }
    finally { setBusy(false); }
  };

  const title = challengeId ? "Confirme seu acesso" : mode === "request" ? "Recuperar acesso" : mode === "reset" ? "Defina sua nova senha" : "Entrar";

  return <AuthScreenLayout>
    <form className="login-form" onSubmit={submit}>
        <header className="login-form-header">
          <h1>{title}</h1>
          {mode === "request" && <p>Informe o e-mail vinculado à sua conta.</p>}
          {mode === "reset" && <p>Use no mínimo 12 caracteres.</p>}
        </header>

        <div className="login-fields">
          {challengeId ? <MfaCodeInput digits={codeDigits} onChange={setCodeDigits} disabled={busy} /> : mode === "request" ?
            <div className="auth-field">
              <label htmlFor={emailId}>E-mail</label>
              <div className="auth-input-control"><Mail size={18} /><input id={emailId} type="email" autoComplete="email" value={email} onChange={(event) => setEmail(formatEmailInput(event.target.value))} required autoFocus /></div>
            </div> : mode === "reset" ?
            <div className="auth-field">
              <label htmlFor={newPasswordId}>Nova senha</label>
              <div className="auth-input-control"><LockKeyhole size={18} /><input id={newPasswordId} type={passwordVisible ? "text" : "password"} autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={12} required autoFocus /><button type="button" onClick={() => setPasswordVisible((visible) => !visible)} aria-label={passwordVisible ? "Ocultar senha" : "Mostrar senha"}>{passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>
            </div> : <>
            <div className="auth-field">
              <label htmlFor={emailId}>E-mail</label>
              <div className="auth-input-control"><Mail size={18} /><input id={emailId} type="email" autoComplete="username" value={email} onChange={(event) => setEmail(formatEmailInput(event.target.value))} required autoFocus /></div>
            </div>
            <div className="auth-field">
              <label htmlFor={passwordId}>Senha</label>
              <div className="auth-input-control"><LockKeyhole size={18} /><input id={passwordId} type={passwordVisible ? "text" : "password"} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={12} required /><button type="button" onClick={() => setPasswordVisible((visible) => !visible)} aria-label={passwordVisible ? "Ocultar senha" : "Mostrar senha"}>{passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>
            </div>
          </>}
        </div>

        {error && <p className="form-error" role="alert">{error}</p>}
        {notice && <p className="login-notice" role="status">{notice}</p>}
        <button className="site-button site-button-red login-submit" disabled={busy || Boolean(challengeId && !mfaComplete)}>{busy ? "Aguarde…" : challengeId ? "Confirmar código" : mode === "request" ? "Enviar instruções" : mode === "reset" ? "Salvar senha" : "Entrar"}</button>
        {!challengeId && mode === "login" && biometricsAvailable && <><div className="login-auth-divider"><span>ou</span></div><button type="button" className="site-button site-button-outline login-passkey" onClick={() => void biometricLogin()} disabled={busy}><Fingerprint size={19} /> Entrar com biometria</button></>}
        {!challengeId && mode === "login" && <button type="button" className="login-link" onClick={() => { setMode("request"); setError(""); setNotice(""); }}>Esqueci minha senha</button>}
        {(challengeId || mode !== "login") && <button type="button" className="login-back" onClick={returnToLogin}><ArrowLeft size={16} /> Voltar ao login</button>}
    </form>
  </AuthScreenLayout>;
}
